import { $getRoot, $getSelection, $isRangeSelection, $isParagraphNode, $createParagraphNode } from 'lexical';
import { $isColumnNode } from '../Nodes/ColumnNode';

// Strips the second entity layer left by double-encoded legacy content; <textarea> decodes without interpreting tags.
export function decodeHtmlEntities(text) {
    if (!text || text.indexOf('&') === -1) return text;
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
}

const sanitizedEntities = { amp: '&', nbsp: ' ', lt: '<', gt: '>' };

// Description sections were saved through an HTML sanitizer that entity-encoded the Lexical JSON (literal "&nbsp;",
// "&amp;"). Only these four entities: each decodes to a character valid inside a JSON string, so the JSON stays parseable.
export function decodeSanitizedEntities(json) {
    return json.replace(/&(amp|nbsp|lt|gt);/g, (match, name) => sanitizedEntities[name]);
}

// An empty column can't take a caret, so keep a paragraph; call after remove/reparent with the old parent.
export function $ensureColumnNotEmpty(parent) {
    if ($isColumnNode(parent) && parent.getChildrenSize() === 0) {
        parent.append($createParagraphNode());
    }
}

export function $insertEmbeddableNode(node) {
    const selection = $getSelection();

    if (!$isRangeSelection(selection)) {
        // No caret (editor never focused): append at the end rather than dropping the node silently.
        $getRoot().append(node);
    }
    else {
        const topLevelElement = selection.anchor.getNode().getTopLevelElementOrThrow();

        // Replace an empty paragraph instead of inserting after it, or stray paragraphs break flush stacking.
        if ($isParagraphNode(topLevelElement) && topLevelElement.getChildrenSize() === 0) {
            topLevelElement.replace(node);
        }
        else {
            topLevelElement.insertAfter(node);
        }
    }

    const next = node.getNextSibling();
    if (next === null) {
        const paragraph = $createParagraphNode();
        node.insertAfter(paragraph);
        paragraph.select();
    }
    else if (typeof next.selectStart === 'function') {
        next.selectStart();
    }
}

export function isEmptyParagraphJson(node) {
    if (node.type !== 'paragraph') return false;
    if (!node.children || node.children.length === 0) return true;
    return node.children.every(child => child.type === 'text' && child.text === '');
}

export function stripTrailingEmptyParagraphs(stateJson) {
    let json;
    try {
        json = typeof stateJson === 'string' ? JSON.parse(stateJson) : stateJson;
    }
    catch {
        return stateJson;
    }

    const children = json.root?.children;
    if (!Array.isArray(children)) return stateJson;

    while (children.length > 1 && isEmptyParagraphJson(children[children.length - 1])) {
        children.pop();
    }

    return JSON.stringify(json);
}
